import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "createNewFooter", "defaultOptions", "dropdownClassName", "error", "extraDropdownFooterContent", "menuStyle", "options", "placeholder", "readOnly", "type", "useOnDark", "valueRenderer"];
import { PureComponent } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { isEqual, pick } from 'underscore';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UISelect from 'UIComponents/input/UISelect';
import UIAlert from 'UIComponents/alert/UIAlert';
import { Fragment } from 'react';
import { isNullOrUndefined, isObject } from 'ContentUtils/helpers/ObjectHelpers';
import { FAILED } from 'ContentUtils/constants/RequestStatus';
import UIFlex from 'UIComponents/layout/UIFlex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = suffix => `ContentComponents.cmv2.fields.baseResource.${suffix}`;
const isEmptyValue = value => isNullOrUndefined(value) || value === '' || Array.isArray(value) && !value.length;
class AsyncSelector extends PureComponent {
  constructor(props) {
    super(props);
    this.loadOptions = this.loadOptions.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onOpenChange = this.onOpenChange.bind(this);
    this.state = {
      isOpen: false,
      isUpdatingDefaultOptions: false
    };
  }
  componentDidMount() {
    const {
      defaultOptions,
      value
    } = this.props;
    const defaultOptionsKey = Array.isArray(value) ? JSON.stringify(value) : `${value}`;
    const defaultOptionsValueMap = defaultOptions.reduce((acc, option) => {
      const {
        value: defaultOptionValue
      } = option;
      acc[`${defaultOptionValue}`] = defaultOptionValue;
      return acc;
    }, {});
    this.defaultOptionsData = isEmptyValue(value) ? Object.assign({}, defaultOptionsValueMap) : Object.assign({}, defaultOptionsValueMap, {
      [defaultOptionsKey]: value
    });
  }
  componentDidUpdate(prevProps, prevState) {
    const {
      clearCache,
      defaultOptions
    } = this.props;
    const {
      isOpen,
      isUpdatingDefaultOptions
    } = this.state;
    if (!isEqual(prevProps.defaultOptions, defaultOptions)) {
      defaultOptions.reduce((updatedDefaultOptionsData, option) => {
        if ('value' in option && !isEmptyValue(option.value)) {
          const defaultOptionsKey = Array.isArray(option.value) ? JSON.stringify(option.value) : `${option.value}`;
          if (updatedDefaultOptionsData[defaultOptionsKey] === undefined) {
            updatedDefaultOptionsData[defaultOptionsKey] = option.value;
          }
        }
        return updatedDefaultOptionsData;
      }, this.defaultOptionsData);
      this.setState({
        isUpdatingDefaultOptions: false
      });
      clearCache();
    }
    if (!isUpdatingDefaultOptions && prevState.isOpen !== isOpen) {
      clearCache();
    }
  }
  onOpenChange(evt) {
    const {
      onOpenChange
    } = this.props;
    if (onOpenChange) {
      onOpenChange(evt);
    }
    const isOpen = evt.target.value;
    if (!isOpen) {
      this.resetOffset = true;
    }
    this.setState({
      isOpen
    });
  }
  onChange(evt) {
    const {
      clearCache,
      onChange,
      saveCache
    } = this.props;
    const newValue = evt.target.value;
    const defaultOptionsKey = Array.isArray(newValue) ? JSON.stringify(newValue) : `${newValue}`;
    if (!isEmptyValue(newValue) && this.defaultOptionsData[defaultOptionsKey] === undefined) {
      this.setState({
        isUpdatingDefaultOptions: true
      });
      saveCache(evt.target.value);
    } else {
      clearCache();
    }
    onChange(evt);
  }

  /* Proxy load options to reset offset when closed */
  loadOptions(input, callback, pagination = {
    offset: 0
  }) {
    let __pagination = pagination;
    if (this.resetOffset) {
      __pagination = {
        offset: 0
      };
      this.resetOffset = false;
    }
    this.props.loadOptions(input, callback, __pagination);
  }
  renderRequestFailedAlert() {
    const {
      optionsRequestStatus
    } = this.props;
    return optionsRequestStatus === FAILED && /*#__PURE__*/_jsx(UIAlert, {
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(`failedOptionsRequest.title`)
      }),
      type: "danger",
      className: "m-bottom-3",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(`failedOptionsRequest.desc`)
      })
    });
  }
  render() {
    const _this$props = this.props,
      {
        className,
        createNewFooter,
        defaultOptions,
        dropdownClassName,
        error,
        extraDropdownFooterContent,
        menuStyle,
        options,
        placeholder: userPlaceholder,
        readOnly,
        type,
        useOnDark,
        valueRenderer
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      isOpen,
      isUpdatingDefaultOptions
    } = this.state;
    const selectProps = pick(rest,
    // @ts-expect-error UISelect.propTypes does exist at runtime
    Object.keys(UISelect.propTypes));
    let {
      value
    } = this.props;
    if (isObject(value)) {
      value = undefined;
    }
    const __loadOptions = isOpen ? this.loadOptions : undefined;
    const __options = isOpen || isUpdatingDefaultOptions ? options : defaultOptions;
    const placeHolder = userPlaceholder || /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: i18nKey('placeholder'),
      options: {
        type: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey(`typeLabels.${type}`),
          options: {
            count: 1
          }
        })
      }
    });
    const dropdownFooter = createNewFooter || extraDropdownFooterContent ? /*#__PURE__*/_jsxs(UIFlex, {
      direction: "column",
      children: [extraDropdownFooterContent, createNewFooter]
    }) : null;
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [this.renderRequestFailedAlert(), /*#__PURE__*/_jsx(UISelect, Object.assign({
        buttonUse: useOnDark ? 'form-on-dark' : 'form',
        className: className,
        "data-selenium": this.props['data-selenium'],
        "data-test-id": "async-selector",
        "data-value": value,
        dropdownFooter: dropdownFooter,
        dropdownClassName: dropdownClassName,
        error: error,
        menuStyle: menuStyle,
        options: __options,
        placeholder: placeHolder,
        readOnly: readOnly,
        valueRenderer: valueRenderer
      }, selectProps, {
        loadOptions: __loadOptions,
        onChange: this.onChange,
        onOpenChange: this.onOpenChange,
        value: value
      }))]
    });
  }
}
AsyncSelector.defaultProps = {
  cache: null,
  clearCache: emptyFunction,
  defaultOptions: [],
  filterOptions: false,
  options: [],
  saveCache: emptyFunction,
  value: ''
};
export default AsyncSelector;